import { httpsCallable } from 'firebase/functions';
import type {
  BillingResumeSubscriptionRequest,
  BillingResumeSubscriptionResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

const billingResumeSubscriptionCallable = httpsCallable<
  BillingResumeSubscriptionRequest,
  BillingResumeSubscriptionResponse
>(functions, 'billingResumeSubscription');

/**
 * TP-23: `billingResumeSubscription` Callable の薄いラッパ。
 * 解約予約中（status=canceled）/ 一時停止中（status=paused）のサブスクを PAY.JP `subscriptions.resume`
 * で復帰させ、テナントを active に戻す（suspended は解除しない）。失敗時は FunctionsError を throw。
 */
export async function callBillingResumeSubscription(
  input: BillingResumeSubscriptionRequest,
): Promise<BillingResumeSubscriptionResponse> {
  const res = await billingResumeSubscriptionCallable(input);
  return res.data;
}

interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/** `billingResumeSubscription` のエラーを saas-admin 向け日本語メッセージに変換。 */
export function billingResumeSubscriptionErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/already-exists':
        return 'このサブスクは既に稼働中です（再開不要）。';
      case 'functions/failed-precondition':
        return `再開できません：${err.message ?? '再開対象の月額サブスクがありません'}`;
      case 'functions/aborted':
        return (
          err.message ??
          'PAY.JP の再開処理に失敗しました。時間をおいて再度お試しください。'
        );
      case 'functions/not-found':
        return '対象のテナントが見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `再開に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `再開に失敗しました：${err.message}`;
  }
  return '再開に失敗しました。';
}
